import type { Router } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { routes, preloadRoutes } from './routes'

const getFirstAccessiblePath = (userStore: ReturnType<typeof useUserStore>): string | undefined => {
  const layout = routes.find((route) => route.path === '/')
  const target = layout?.children?.find((route) => {
    const required = route.meta?.permissions ?? []
    return !route.meta?.hidden && userStore.hasEveryPermission(required)
  })
  if (!target) return undefined
  return target.path.startsWith('/') ? target.path : `/${target.path}`
}

// 路由预加载缓存
const preloadedRoutes = new Set<string>()

const preloadRoute = (router: Router, routeName: string) => {
  if (preloadedRoutes.has(routeName)) return
  preloadedRoutes.add(routeName)
  
  const route = router.resolve({ name: routeName })
  if (route.matched.length > 0) {
    // 触发组件预加载
    const components = route.matched.map((record) => record.components?.default).filter(Boolean)
    components.forEach((component) => {
      if (typeof component === 'function') {
        // 尝试调用懒加载组件
        try {
          ;(component as () => any)()
        } catch (error) {
          // 忽略预加载错误
        }
      }
    })
  }
}

export const installRouterGuards = (router: Router): void => {
  router.beforeEach((to) => {
    const userStore = useUserStore()
    document.title = to.meta.title ? `${to.meta.title} - 交发云咖` : '交发云咖管理后台'

    if (to.meta.requiresAuth && !userStore.isLoggedIn) {
      return { path: '/login', query: { redirect: to.fullPath } }
    }
    if (to.path === '/login' && userStore.isLoggedIn) return '/dashboard'

    const required = to.meta.permissions ?? []
    if (required.length && !userStore.hasEveryPermission(required)) {
      const fallbackPath = getFirstAccessiblePath(userStore)
      return fallbackPath ?? '/403'
    }
    
    // 预加载常用路由
    if (userStore.isLoggedIn && preloadRoutes.includes(to.name as string)) {
      preloadRoutes.forEach((routeName: string) => {
        if (routeName !== to.name) {
          // 延迟预加载，避免影响当前页面加载
          setTimeout(() => preloadRoute(router, routeName), 500)
        }
      })
    }
    
    return true
  })
}
